import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Share2 } from "lucide-react";
import Eyebrow from "../components/shared/Eyebrow";
import FinalCTA from "../components/sections/FinalCTA";
import WhatsAppButton from "../components/shared/WhatsAppButton";
import { blogService } from "../services/blogService";
import { useRemoteData } from "../hooks/useRemoteData";

/**
 * Article page.
 *
 * The body arrives as HTML that the backend has already filtered against a
 * strict allow-list (functions/src/lib/sanitize.ts) — no scripts, no event
 * handlers, no iframes — which is what makes `dangerouslySetInnerHTML` safe
 * here. The table of contents and reading time are computed server-side, so
 * they cannot drift from the rendered anchors.
 */
export default function BlogArticle() {
  const { slug } = useParams();

  const { data: post, loading } = useRemoteData(() => blogService.bySlug(slug), [slug]);
  const [siblings, setSiblings] = useState([]);

  useEffect(() => {
    blogService.list().then(setSiblings).catch(() => setSiblings([]));
  }, []);

  if (loading) {
    return (
      <section className="bg-ink pt-32 pb-24 md:pt-40" data-testid="blog-article-loading">
        <div className="container-emilio">
          <div className="animate-pulse space-y-6 max-w-3xl" aria-hidden="true">
            <div className="h-3 w-32 bg-charcoal/50 rounded" />
            <div className="h-10 w-3/4 bg-charcoal/50 rounded" />
            <div className="h-3 w-52 bg-charcoal/40 rounded" />
            <div className="aspect-[16/9] bg-charcoal/40 rounded" />
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="h-3 w-full bg-charcoal/30 rounded" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!post) return <Navigate to="/blog" replace />;

  const index = siblings.findIndex((entry) => entry.slug === slug);
  const previous = index > 0 ? siblings[index - 1] : null;
  const next = index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : null;

  const related = post.related?.length ? post.related : [];
  const toc = post.toc ?? [];

  return (
    <>
      <section className="bg-ink pt-32 pb-12 md:pt-40" data-testid="blog-article">
        <div className="container-emilio">
          <div className="flex items-center gap-2 text-xs text-mutedtext">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-white">Blog</Link>
            <span>/</span>
            <span className="text-white truncate">{post.title}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
            <div className="lg:col-span-8">
              {post.category && <Eyebrow>{post.category}</Eyebrow>}

              <h1 className="mt-4 font-display font-extrabold text-white text-hero max-w-3xl">
                {post.title}
              </h1>

              <div className="mt-6 flex flex-wrap gap-4 text-xs text-mutedtext uppercase tracking-[0.16em]">
                {post.publishedAt && (
                  <span>
                    {new Date(post.publishedAt).toLocaleDateString("en-SG", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                )}
                <span>·</span>
                <span>{post.readingTime} min read</span>
                {post.author && (
                  <>
                    <span>·</span>
                    <span>{post.author}</span>
                  </>
                )}
              </div>

              {post.featuredImage && (
                <div className="mt-10 relative aspect-[16/9] overflow-hidden border border-charcoal">
                  <img
                    src={post.featuredImage}
                    alt={post.imageAltText || ""}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              )}

              <article
                className="prose-emilio article-body mt-10 text-white/90 text-base md:text-lg leading-[1.8] max-w-3xl"
                // Safe: sanitised server-side against an allow-list before storage.
                dangerouslySetInnerHTML={{ __html: post.contentHtml || "" }}
              />

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <WhatsAppButton label="Ask us a question on WhatsApp" />
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `${post.title} — read at ${typeof window !== "undefined" ? window.location.href : ""}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white/80 hover:text-emerald text-sm"
                  data-testid="blog-share"
                >
                  <Share2 className="w-4 h-4" /> Share
                </a>
              </div>

              {(previous || next) && (
                <div className="mt-16 flex items-center justify-between gap-6 border-t border-charcoal pt-8 text-sm">
                  {previous ? (
                    <Link
                      to={`/blog/${previous.slug}`}
                      className="text-white/80 hover:text-emerald flex items-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4 shrink-0" /> {previous.title}
                    </Link>
                  ) : (
                    <span />
                  )}
                  {next ? (
                    <Link
                      to={`/blog/${next.slug}`}
                      className="text-white/80 hover:text-emerald flex items-center gap-2 text-right"
                    >
                      {next.title} <ArrowUpRight className="w-4 h-4 shrink-0" />
                    </Link>
                  ) : (
                    <span />
                  )}
                </div>
              )}
            </div>

            <aside className="lg:col-span-4 space-y-8">
              <div className="border border-charcoal p-6 sticky top-28">
                <div className="text-eyebrow font-display font-bold uppercase tracking-[0.22em] text-emerald">
                  Table of Contents
                </div>
                <ol className="mt-4 space-y-2 text-sm">
                  {toc.length === 0 ? (
                    <li className="text-mutedtext">Introduction</li>
                  ) : (
                    toc.map((entry, entryIndex) => (
                      <li key={entry.id} className={entry.level === 3 ? "pl-4" : ""}>
                        <a href={`#${entry.id}`} className="text-white/85 hover:text-emerald">
                          <span className="text-emerald font-mono mr-2">
                            {String(entryIndex + 1).padStart(2, "0")}
                          </span>
                          {entry.text}
                        </a>
                      </li>
                    ))
                  )}
                </ol>
              </div>

              {related.length > 0 && (
                <div className="border border-charcoal p-6">
                  <div className="text-eyebrow font-display font-bold uppercase tracking-[0.22em] text-emerald">
                    Related Reading
                  </div>
                  <ul className="mt-4 space-y-4">
                    {related.map((entry) => (
                      <li key={entry.id}>
                        <Link to={`/blog/${entry.slug}`} className="group block">
                          <span className="text-sm text-white/85 group-hover:text-emerald">
                            {entry.title}
                          </span>
                          <span className="block text-xs text-mutedtext mt-1">
                            {entry.readingTime} min read
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
